import Util from './util';
import * as log from 'loglevel';


class ConditionsEngine {
    constructor(options, data = {}) {
        // Default settings
        let defaults = {};

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        //  data storage to evaluate conditions against ( INFO: engine.data cannot be static because we have to support multiple products on a single page )
        this.data = data;
    }

    evaluateConditions(conditions, mode, identifier) {

        // mode ( OR = 1 / AND = 2 )
        let modeName = (mode === 1 ? 'OR' : 'AND');

        let level = log.getLevel();

        if (level <= log.levels.TRACE) {
            log.debug('====> evaluating conditions for: ' + identifier + ' in mode ' + modeName, conditions);
        }

        let result = false;

        if(conditions.length){
            for (let i = 0; i < conditions.length; i++) {
                const condition = conditions[i];

                // evaluate with data
                result = this.evaluateCondition(condition, this.data);

                if (level <= log.levels.TRACE) {
                    log.debug('evaluated condition: ', condition, result);
                }

                if(mode === 1){
                    if(result){
                        break;
                    }
                }
                // AND mode break at the first false evaluation
                else if(!result) {
                    break;
                }
            }
        } else {
            // no conditions = match
            result = true;
        }

        log.debug('======> evaluated conditions for: ' + identifier + ' in mode ' + modeName, result);

        // NEW: IF to make sure we always return a boolean value
        if(result){
            return true;
        } else {
            return false;
        }
    }

    evaluateCondition(condition, data){

        if(condition.data === 'options'){
            return this.evaluateOptionCondition(condition, data['options']) === true; // NEW: true check to map -1 (not found)
        } else if(condition.data === 'product'){
            return this.evaluateProductCondition(condition, data['product']);
        } else if(condition.data === 'variant'){
            return this.evaluateVariantCondition(condition, data['variant']);
        }

        return false;
    }


    evaluateOptionCondition(condition, options) {

        let hasTemplateMatch = false;

        // find the option value
        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // find matching option and evaluate
            if(option.name === condition.field){

                let optionValue = option.value;

                // NEW: dynamic value from controller (required to add conditions on library categories)
                if(option._controller && typeof option._controller.conditionValue === 'function'){
                    optionValue = option._controller.conditionValue(option);
                }

                // OR support via ","  INFO: OR between different options is not supported - for this we would need the nested conditions definition
                if(condition.value && condition.value.indexOf(',') !== -1){
                    let values = condition.value.split(',');
                    for (let j = 0; j < values.length; j++) {
                        const value = values[j];
                        // evaluate and return true if first OR condition matches
                        if(this.evaluateOperation(condition.operator, value, optionValue)){
                            return true;
                        }
                    }
                }

                // NEW: option multiSelect auto split option values and check condition against each selected value
                // INFO: this avoids the need for "contains" conditions with multi select + required for Overlay values nested layers connected to multi select option
                // NEW: exclude NEGATED conditions - this will always be true when multiple values are selected - for multi selection !contains is required
                if(condition.operator.indexOf('!') !== 0 && condition.value && optionValue && option._controller && option._controller.multiSelect && optionValue.indexOf(',') !== -1){
                    let values = optionValue.split(',');
                    for (let j = 0; j < values.length; j++) {
                        const value = values[j];
                        // evaluate and return true if any value matches
                        if(this.evaluateOperation(condition.operator, condition.value, value)){
                            return true;
                        }
                    }
                    // all values already checked no need for default below
                    return false;
                }

                // default INFO: also executed for OR if for some reason the explicit value contains ","
                return this.evaluateOperation(condition.operator, condition.value, optionValue);
            }

            // Group-editor template name match (OR across instances)
            if(option._templateName === condition.field){
                hasTemplateMatch = true;
                let optionValue = option.value;

                if(option._controller && typeof option._controller.conditionValue === 'function'){
                    optionValue = option._controller.conditionValue(option);
                }

                // OR: if ANY instance satisfies, return true immediately
                if(this.evaluateOperation(condition.operator, condition.value, optionValue)){
                    return true;
                }
                continue; // check remaining instances
            }

            // support for nested options
            else if(option.options && option.options.length > 0){

                let result = this.evaluateOptionCondition(condition, option.options);

                // NEW: only return true if nested condition actually found the option (to support multiple levels of nesting - e.g personalize group > group > option)
                if(result !== -1) {
                    return result;
                }
            }
        }

        // Template instances found but none satisfied → false (condition not met)
        if(hasTemplateMatch) return false;

        return -1; // NEW: return -1 if no option found (to search all nested options)
    }


    evaluateVariantCondition(condition, variant) {

        if(variant){
            switch (condition.field) {
                case 'variant':
                    if(condition.variants){
                        for (let i = 0; i < condition.variants.length; i++) {
                            if (condition.variants[i] && condition.variants[i].id.toString() === variant.id.toString()) {
                                return true;
                            }
                        }
                    }

                    break;
                case 'variant_title':

                    // NEW: OR support via ","
                    if(condition.value && condition.value.indexOf(',') !== -1){
                        let values = condition.value.split(',');
                        for (let j = 0; j < values.length; j++) {
                            const value = values[j];
                            // evaluate and return true if first OR condition matches
                            if(this.evaluateOperation(condition.operator, value, variant.title)){
                                return true;
                            }
                        }
                    } else if (this.evaluateOperation(condition.operator, condition.value, variant.title)) {
                        return true;
                    }

                    break;
            }
        }

        return false;
    }

    evaluateProductCondition(condition, product) {
        switch (condition.field) {
            case 'product':
                for (let i = 0; i < condition.products.length; i++) {
                    if (condition.products[i] && condition.products[i].id.toString() === product.id.toString()) {
                        return true;
                    }
                }
                break;

            case 'product_handle':
                if (this.evaluateOperation(condition.operator, condition.value, product.handle)) {
                    return true;
                }
                break;

            case 'product_title':
                if (this.evaluateOperation(condition.operator, condition.value, product.title)) {
                    return true;
                }
                break;
            case 'product_type':

                // support json  / js view
                if (product.type) {
                    if (this.evaluateOperation(condition.operator, condition.value, product.type)) {
                        return true;
                    }
                }
                if (product.product_type) {
                    if (this.evaluateOperation(condition.operator, condition.value, product.product_type)) {
                        return true;
                    }
                }

                break;

            case 'product_vendor':
                if (this.evaluateOperation(condition.operator, condition.value, product.vendor)) {
                    return true;
                }
                break;
            case 'product_tags':

                let tags;

                // js view is array
                if(Array.isArray(product.tags)) {
                    tags = product.tags
                } else if(product.tags) {
                    if(product.tags.indexOf(', ') >= 0) {
                        tags = product.tags.split(', ');
                    }
                    else {
                        tags = [ product.tags ];
                    }
                } else {
                    break;
                }

                for (let i = 0; i < condition.tags.length; i++) {
                    for (let k = 0; k < tags.length; k++) {
                        if (
                            condition.tags[i]
                                .toString()
                                .toLowerCase()
                                .trim() ===
                            tags[k]
                                .toString()
                                .toLowerCase()
                                .trim()
                        ) {
                            return true;
                        }
                    }
                }
                break;

        }
        return false;
    }

    evaluateOperation(operator, value, input) {
        if (typeof input !== 'undefined') {
            switch (operator) {
                // string operations
                case 'equals':
                    return input.toString().toLowerCase().trim() === value?.toString().toLowerCase().trim();
                case '!equals':
                    return input.toString().toLowerCase().trim() !== value?.toString().toLowerCase().trim();
                case 'contains':
                    return input.toString().toLowerCase().trim().indexOf(value?.toString().toLowerCase().trim()) > -1;
                case '!contains':
                    return input.toString().toLowerCase().trim().indexOf(value?.toString().toLowerCase().trim()) === -1;
                case 'empty':
                    return !input; // no value
                case '!empty':
                    return !!input; // value ( !! important to make sure a boolean is returned )
                // number operations
                case '=':
                    return parseFloat(input) === parseFloat(value);
                case '!=':
                    return parseFloat(input) !== parseFloat(value);
                case '>':
                    return parseFloat(input) > parseFloat(value);
                case '>=':
                    return parseFloat(input) >= parseFloat(value);
                case '<':
                    return parseFloat(input) < parseFloat(value);
                case '<=':
                    return parseFloat(input) <= parseFloat(value);
            }

            return false;
        }
    }

}

ConditionsEngine.MODE = { OR: 1, AND: 2 };

export default ConditionsEngine;